import { create } from 'zustand';
import { ActorAction, ActorState, StationDef, WorldSnapshot } from './types';
import { DEFAULT_STATIONS, DEFAULT_STATIONS_AS_LOGICAL } from './layout';

// ─── Seed actors so the scene is alive before the backend hydrates ───
// The real server snapshot arrives via `sim:init` and replaces these.
// Until then we render the full team so the 3D world never looks empty.
const seedActors = (): Record<string, ActorState> => {
  const mk = (
    id: string,
    name: string,
    role: string,
    stationId: string,
    color: string,
    mood: ActorState['mood']
  ): ActorState => {
    const st = DEFAULT_STATIONS[stationId];
    return {
      id, name, role,
      status: 'idle',
      action: 'idle',
      stationId,
      position: st ? { ...st.position } : { x: 0, y: 0, z: 0 },
      facing: st?.facing ?? 0,
      color,
      mood,
      lastEventAt: new Date().toISOString(),
    };
  };
  const map: Record<string, ActorState> = {};
  const seed = [
    mk('claude-coder',     'Claude', 'coder',        'desk-coder',        '#8b5cf6', 'focused'),
    mk('claw-orchestrator', 'Claw',   'orchestrator', 'desk-orchestrator', '#f97316', 'thinking'),
    mk('claude-tester',    'Tess',   'tester',       'desk-tester',       '#06b6d4', 'relaxed'),
    mk('miro-architect',   'Mira',   'architect',    'desk-architect',    '#10b981', 'focused'),
  ];
  for (const a of seed) map[a.id] = a;
  return map;
};

// ═══════════════════════════════════════════════════════════
//  Simulation Store — zustand
//  Single source of truth for the 3D scene. WebSocket events
//  mutate it; R3F components subscribe via selectors and
//  re-render only what actually changed.
// ═══════════════════════════════════════════════════════════

export interface SpeechBubble {
  id: string;
  actorId: string;
  text: string;
  expiresAt: number; // Date.now() + ms
}

export interface CommitPing {
  id: string;
  actorId: string;
  hash: string;
  message: string;
  expiresAt: number;
}

interface SimState {
  ready: boolean;                            // got a sim:init yet?
  stations: Record<string, StationDef>;
  actors: Record<string, ActorState>;
  selectedActorId: string | null;
  activeCamera: string;                      // key from CAMERA_PRESETS
  bubbles: SpeechBubble[];
  commitPings: CommitPing[];
  alerts: { id: string; text: string; expiresAt: number }[];

  // actions
  hydrate: (snapshot: WorldSnapshot) => void;
  setActor: (actor: ActorState) => void;
  patchActor: (id: string, patch: Partial<ActorState>) => void;
  setAction: (id: string, action: ActorAction) => void;
  setProgress: (id: string, progress: number) => void;
  addBubble: (actorId: string, text: string, ttlMs?: number) => void;
  addCommit: (actorId: string, hash: string, message: string) => void;
  addAlert: (text: string, ttlMs?: number) => void;
  select: (id: string | null) => void;
  setCamera: (name: string) => void;
  gc: () => void; // evict expired bubbles / commits / alerts
}

const now = () => Date.now();
const uid = () => Math.random().toString(36).substring(2, 10);

export const useSimStore = create<SimState>((set, get) => ({
  ready: false,
  stations: DEFAULT_STATIONS_AS_LOGICAL,
  actors: seedActors(),
  selectedActorId: null,
  activeCamera: 'overview',
  bubbles: [],
  commitPings: [],
  alerts: [],

  hydrate: (snapshot) => {
    const actorMap: Record<string, ActorState> = {};
    for (const a of snapshot.actors) actorMap[a.id] = a;
    set({
      ready: true,
      stations: snapshot.stations && Object.keys(snapshot.stations).length ? snapshot.stations : DEFAULT_STATIONS_AS_LOGICAL,
      actors: actorMap,
    });
  },

  setActor: (actor) => {
    set((s) => ({ actors: { ...s.actors, [actor.id]: actor } }));
  },

  patchActor: (id, patch) => {
    set((s) => {
      const cur = s.actors[id];
      if (!cur) return s;
      return { actors: { ...s.actors, [id]: { ...cur, ...patch } } };
    });
  },

  setAction: (id, action) => {
    set((s) => {
      const cur = s.actors[id];
      if (!cur) return s;
      return { actors: { ...s.actors, [id]: { ...cur, action, lastEventAt: new Date().toISOString() } } };
    });
  },

  setProgress: (id, progress) => {
    set((s) => {
      const cur = s.actors[id];
      if (!cur || !cur.currentTask) return s;
      return {
        actors: {
          ...s.actors,
          [id]: { ...cur, currentTask: { ...cur.currentTask, progress } },
        },
      };
    });
  },

  addBubble: (actorId, text, ttlMs = 4000) => {
    set((s) => ({
      bubbles: [
        ...s.bubbles,
        { id: uid(), actorId, text, expiresAt: now() + ttlMs },
      ].slice(-30),
    }));
  },

  addCommit: (actorId, hash, message) => {
    set((s) => ({
      commitPings: [
        ...s.commitPings,
        { id: uid(), actorId, hash, message, expiresAt: now() + 5000 },
      ].slice(-20),
    }));
  },

  addAlert: (text, ttlMs = 6000) => {
    set((s) => ({
      alerts: [
        ...s.alerts,
        { id: uid(), text, expiresAt: now() + ttlMs },
      ].slice(-10),
    }));
  },

  select: (id) => set({ selectedActorId: id }),
  setCamera: (name) => set({ activeCamera: name }),

  gc: () => {
    const t = now();
    const s = get();
    const bubbles = s.bubbles.filter((b) => b.expiresAt > t);
    const commitPings = s.commitPings.filter((c) => c.expiresAt > t);
    const alerts = s.alerts.filter((a) => a.expiresAt > t);
    if (bubbles.length !== s.bubbles.length || commitPings.length !== s.commitPings.length || alerts.length !== s.alerts.length) {
      set({ bubbles, commitPings, alerts });
    }
  },
}));

// ─── Selectors ─────────────────────────────────────────
// ⚠ DO NOT create derived collections (Object.values/keys, .map, .filter)
// inside a zustand selector — every call returns a new reference, the
// default Object.is equality check sees an "always-changed" snapshot, and
// React enters an infinite re-render loop (minified error #185).
// Subscribe to the raw map and derive with useMemo in the component:
//   const actorsMap = useSimStore((s) => s.actors);
//   const actors = useMemo(() => Object.values(actorsMap), [actorsMap]);
export const selectActor  = (id: string) => (s: SimState): ActorState | undefined => s.actors[id];
export const selectStation = (id: string) => (s: SimState): StationDef | undefined => s.stations[id];
